```vue
<template>
  <div class="canvas-container">
    <canvas v-bind:id="canvasId" tabIndex="1"></canvas>
  </div>
</template>

<script>
import logger from '@/utils/logger';

export default {
  props: {
    patchDir: String,
    canvasId: {
      type: String,
      default: 'glcanvas'
    },
    patchOptions: Object,
    projectsData: Object
  },
  
  data() {
    return {
      isWarmupPeriod: true,
      lastBlinkTime: 0
    }
  },
  
  mounted() {
    // Preload MediaPipe script
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'script';
    link.href = 'https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh@0.4.1633559619/face_mesh.js';
    document.head.appendChild(link);
    
    this.initializePatch();
    window.addEventListener('beforeunload', this.cleanup);
  },

  beforeDestroy() {
    window.removeEventListener('beforeunload', this.cleanup);
  },
  
  methods: {
    async initializeVideo() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ 
          video: { 
            facingMode: 'user',
            width: { ideal: 1280 },
            height: { ideal: 720 }
          } 
        });
        
        const video = document.createElement('video');
        video.srcObject = stream;
        video.id = 'webcamVideo';
        video.autoplay = true;
        video.playsInline = true;
        video.muted = true;
        // Make video completely hidden
        video.style.cssText = 'position: absolute; pointer-events: none; opacity: 0; width: 1px; height: 1px; overflow: hidden; clip: rect(1px, 1px, 1px, 1px); clip-path: inset(50%);';
        document.body.appendChild(video);
        
        // Wait for video to be ready
        await new Promise((resolve) => {
          video.onloadedmetadata = () => {
            video.play().then(resolve);
          };
        });
        
        return { video, stream };
      } catch (err) {
        console.error('Failed to initialize video:', err);
        throw err;
      }
    },

    async initializePatch() {
      try {
        // Initialize video first
        const { video, stream } = await this.initializeVideo();
        
        // Create canvas and get context early
        const canvas = document.getElementById(this.canvasId);
        if (!canvas) {
          throw new Error('Canvas element not found');
        }

        // Initialize WebGL context with optimized settings
        const contextAttributes = {
          alpha: true,
          antialias: false,
          depth: true,
          failIfMajorPerformanceCaveat: false,
          powerPreference: 'high-performance',
          premultipliedAlpha: false,
          preserveDrawingBuffer: true,
          stencil: true,
          desynchronized: true
        };

        const gl = canvas.getContext('webgl2', contextAttributes);
        if (!gl) {
          throw new Error('WebGL2 not available');
        }

        // Enable necessary extensions early
        gl.getExtension('EXT_color_buffer_float');
        gl.getExtension('OES_texture_float_linear');

        // Load MediaPipe Face Mesh
        await new Promise((resolve, reject) => {
          const mediaPipeScript = document.createElement('script');
          mediaPipeScript.src = 'https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh@0.4.1633559619/face_mesh.js';
          mediaPipeScript.async = true;
          mediaPipeScript.onload = () => {
            if (window.FaceMesh) {
              window.faceMesh = new window.FaceMesh({
                locateFile: (file) => {
                  return `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh@0.4.1633559619/${file}`;
                }
              });
              
              const faceDetectionSettings = {
                maxNumFaces: 1,
                refineLandmarks: false,
                minDetectionConfidence: 0.5,
                minTrackingConfidence: 0.5
              };

              window.faceMesh.setOptions(faceDetectionSettings);
            }
            resolve();
          };
          mediaPipeScript.onerror = reject;
          document.body.appendChild(mediaPipeScript);
        });

        // Load patch with optimized settings
        await new Promise((resolve, reject) => {
          const patchScript = document.createElement('script');
          patchScript.src = `${this.patchDir}/js/patch.js`;
          patchScript.async = true;
          patchScript.onload = () => {
            try {
              const options = {
                patch: window.CABLES.exportedPatch,
                prefixAssetPath: this.patchDir + '/',
                jsPath: this.patchDir + '/js/',
                glCanvasId: this.canvasId,
                glCanvasResizeToWindow: false,
                canvas: {
                  gl,
                  ...contextAttributes
                },
                onFinishedLoading: () => {
                  logger.log(this.patchDir + ' finished loading');
                  this.$emit('patch-loaded');
                },
                onError: (err) => {
                  console.error('Patch error:', err);
                },
                variables: {
                  inputJson: this.projectsData,
                  HiresDisplay: Math.min(window.devicePixelRatio || 1, 2),
                  showUI: 0,
                  enableWebcam: 1,
                  showWebcam: 1,
                  displayEyes: 1,
                  warmupDelay: 500,
                  minTimeBetweenBlinks: 500,
                  faceDetectionConfidenceThreshold: 0.85,
                  requiredStableFrames: 3,
                  videoElement: video,
                  stream: stream
                },
                ...this.patchOptions
              };

              window.CABLES.patch = new window.CABLES.Patch(options);
              resolve();
            } catch (err) {
              reject(err);
            }
          };
          patchScript.onerror = reject;
          document.body.appendChild(patchScript);
        });
      } catch (err) {
        console.error('Failed to initialize patch:', err);
        throw err;
      }
    },

    async cleanup() {
      try {
        // Pause animations first
        if (window.CABLES?.patch) {
          window.CABLES.patch.pause();
          window.cancelAnimationFrame(window.CABLES.patch._animFrameHandle);
          
          // Wait for frame to complete
          await new Promise(resolve => setTimeout(resolve, 50));
          
          // Reset context state
          const canvas = document.getElementById(this.canvasId);
          if (canvas) {
            const gl = canvas.getContext('webgl2');
            if (gl) {
              try {
                gl.flush();
                gl.finish();
                gl.bindFramebuffer(gl.FRAMEBUFFER, null);
                gl.bindRenderbuffer(gl.RENDERBUFFER, null);
                gl.bindBuffer(gl.ARRAY_BUFFER, null);
                gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, null);
                gl.bindTexture(gl.TEXTURE_2D, null);
                gl.useProgram(null);
                gl.disable(gl.DEPTH_TEST);
                gl.disable(gl.BLEND);
                gl.disable(gl.CULL_FACE);
                gl.disable(gl.STENCIL_TEST);
                gl.getExtension('WEBGL_lose_context')?.loseContext();
              } catch (e) {
                // Ignore WebGL errors during cleanup
              }
            }
          }
          
          window.CABLES.patch.dispose();
          window.CABLES.patch = null;
        }

        await this.$nextTick();
      } catch (e) {
        console.warn('Cleanup error:', e);
      }
    },

    handleBlinkEvent(event) {
      if (this.isWarmupPeriod) {
        return; // Ignore blinks during warmup
      }
      
      const now = Date.now();
      if (now - this.lastBlinkTime < 500) {
        return; // Ignore blinks that are too close together
      }
      
      this.lastBlinkTime = now;
      // Handle valid blink here
      this.$emit('blink-detected');
    }
  },
  
  beforeUnmount() {
    this.cleanup();
  }
};
</script>

<style>
.canvas-container {
  height: 100vh;
  width: 100vw;
  overflow: hidden;
  background: transparent;
  position: fixed;
  top: 0;
  left: 0;
}

canvas {
  width: 100% !important;
  height: 100% !important;
  background: transparent !important;
}

@media (max-width: 768px) {
  .canvas-container {
    height: calc(100vh - env(safe-area-inset-bottom, 0px));
  }
  
  canvas {
    height: calc(100vh - env(safe-area-inset-bottom, 0px)) !important;
  }
}
</style>
```